import { useMemo } from 'react';
import { connect, useDispatch } from 'react-redux';
import * as R from 'ramda';

import { ISelectOption } from 'UIPackage/MainModule/components/SelectInput';
import { captchaSetActionAfterTokenLoad, captchaSetIsActive } from 'CorePackage/CaptchaModule/actions/captchaActions';
import { getCaptchaToken } from 'CorePackage/CaptchaModule/selectors/captchaSelectors';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';

import { searchFilterMapper } from '../common/searchFilterMapper';
import styles from './SearchFilters.module.scss';
import { searchGetResultData, searchSetFilter } from '../actions/searchActions';
import { SearchFilterString } from './SearchFilterString';
import { SearchFilterArray } from './SearchFilterArray';
import { SearchFilterObject } from './SearchFilterObject';
import { SearchFilterDates } from './SearchFilterDates';
import { IKnmSearchInitFilter } from '../interfaces/IKnmSearchInitFilter';

interface ISearchFiltersProps {
  filterValues: Partial<IKnmSearchInitFilter>;
}

interface ISearchFiltersStoreProps {
  captchaToken: string | null;
}

const SearchFiltersWithoutStore = ({ filterValues, captchaToken }: ISearchFiltersProps & ISearchFiltersStoreProps) => {
  const dispatch = useDispatch();
  const otherFilters = useMemo(() => R.omit(['fromDate', 'toDate'], filterValues), [filterValues]);
  const stringFilters = useMemo(() => R.filter(x => typeof x === 'string', otherFilters), [otherFilters]) as Record<
    string,
    string
  >;
  const arrayFilters = useMemo(() => R.filter(x => Array.isArray(x), otherFilters), [otherFilters]) as Record<
    string,
    ISelectOption<string | number>[]
  >;
  const objectFilters = useMemo(
    () => R.filter(x => !Array.isArray(x) && typeof x !== 'string', otherFilters),
    [otherFilters],
  ) as Record<string, ISelectOption<string | number>>;

  const removeFilterValue = (id: string | string[]) => () => {
    const ids = Array.isArray(id) ? id : [id];
    dispatch(searchSetFilter(R.omit(ids, filterValues)));
    if (!captchaToken) {
      dispatch(captchaSetActionAfterTokenLoad(searchGetResultData));
      dispatch(captchaSetIsActive(true));
    } else {
      dispatch(searchGetResultData());
    }
  };

  return (
    <div className={styles.Filters}>
      {Object.keys(stringFilters).map(filter => (
        <SearchFilterString
          key={filter}
          filterName={searchFilterMapper(filter)}
          filterValue={stringFilters[filter]}
          removeFilterValue={removeFilterValue(filter)}
        />
      ))}
      {Object.keys(arrayFilters).map(filter => (
        <SearchFilterArray
          key={filter}
          filterName={filter}
          filter={filterValues}
          filterValue={arrayFilters[filter]}
          captchaToken={captchaToken}
        />
      ))}
      {Object.keys(objectFilters).map(filter => (
        <SearchFilterObject
          key={filter}
          filterName={searchFilterMapper(filter)}
          filterValue={objectFilters[filter]}
          removeFilterValue={removeFilterValue(filter)}
        />
      ))}
      {filterValues.fromDate && filterValues && (
        <SearchFilterDates
          fromDate={filterValues.fromDate}
          toDate={filterValues.toDate}
          removeFilterValue={removeFilterValue}
        />
      )}
    </div>
  );
};

export const SearchFilters = connect(
  (state: IRootState): ISearchFiltersStoreProps => ({
    captchaToken: getCaptchaToken(state),
  }),
)(SearchFiltersWithoutStore);
